import { yupResolver } from '@hookform/resolvers/yup';
import { range } from 'lodash';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { OptionType } from '../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { useSaveDeferredCompensationMutation } from '../../api/useSaveDeferredCompensationMutation';
import {
  DeferredCompensationContributionUnit,
  DeferredCompensationDTO,
} from '../../tables/DeferredCompensation/DeferredCompensation.meta';
import {
  DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS,
  DEFERRED_COMPENSATION_FORM_SCHEMA,
  DeferredCompensationFormValues,
  PRINCIPAL_ID,
  SaveDeferredCompensationDTO,
} from './DeferredCompensationForm.meta';
import {
  ADD_ASSET_SUCCESS_MESSAGE,
  EDIT_ASSET_SUCCESS_MESSAGE,
  OwnerTypeDTO,
} from '../../OtherAssets.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import {
  EXCLUDE_OPT,
  getAssetBucketOptions,
  getCustomBucketIndex,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { onManagementSelect as baseOnManagementSelect } from '../components/ManagementStyle/ManagementStyle.meta';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';

interface UseDeferredCompensationParams {
  clientId: string;
  asset: DeferredCompensationDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
}

export const useDeferredCompensationForm = ({
  clientId,
  options,
  asset,
  onSuccess,
}: UseDeferredCompensationParams) => {
  const defaultValues: DeferredCompensationFormValues = asset
    ? {
        id: asset.Id,
        name: asset.Name,
        earner: asset.Owner,
        value: asset.Value,
        yearlyContribution: asset.YearlyContribution,
        contributionUnit: asset.ContributionUnit,
        contributionStartAge: asset.ContributionStartAge,
        contributionEndYear: asset.ContributionEndYear,
        distributionStartAge: asset.DistributionStartAge,
        distributionPeriod: asset.DistributionPeriod,
        accumulationGrowthRate: asset.AccumulationGrowthRate,
        distributionGrowthRate: asset.DistributionGrowthRate,
        customBucketIndex: getCustomBucketIndex({
          orionManagementStyleId: asset.OrionManagementStyleId,
          customBucketIndex: asset.CustomBucketIndex,
          managementStyles: options.ManagementStyles,
          isExcludedFromAssetBucketing: false,
          assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
        }),
        isLiquid: asset.IsLiquid,
        includeInGrossEstate: asset.IncludeInGrossEstate,
        orionManagementStyleId: asset.OrionManagementStyleId,
        ownerModel: asset.OwnerModel.map(owner => ({
          ...owner,
          Percentage: (owner.Percentage * 100).toFixed(2),
        })),
      }
    : {
        id: null,
        name: null,
        earner: PRINCIPAL_ID,
        value: null,
        yearlyContribution: null,
        contributionUnit: DeferredCompensationContributionUnit.Dollar,
        contributionStartAge: options.DeferredCompensationOptions.principalAge,
        contributionEndYear: null,
        distributionStartAge: options.DeferredCompensationOptions.principalRetirementAge + 1,
        distributionPeriod: 1,
        accumulationGrowthRate: 2,
        distributionGrowthRate: 2,
        customBucketIndex: null,
        isLiquid: options.DeferredCompensationOptions.IsLiquid,
        includeInGrossEstate: true,
        orionManagementStyleId: null,
        ownerModel: [
          {
            Id: null,
            Name: options.DeferredCompensationOptions.earner.People[0].Name,
            OwnerType: OwnerTypeDTO.Principal,
            Percentage: '100',
          },
        ],
      };

  const defaultContributionUnitOption = DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS.find(
    option => option.value === defaultValues.contributionUnit,
  );

  if (!defaultContributionUnitOption) {
    throw new Error('Invalid deferred compensation contribution unit.');
  }

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<DeferredCompensationFormValues>({
    defaultValues,
    resolver: yupResolver(DEFERRED_COMPENSATION_FORM_SCHEMA),
    context: {
      options: options.DeferredCompensationOptions,
    },
  });

  const { Dob: principalDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const principalYob = principalDob ? new Date(principalDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const [
    ownerModel,
    distributionStartAge,
    contributionUnit,
    contributionEndYear,
    managementStyleSelection,
  ] = watch([
    'ownerModel',
    'distributionStartAge',
    'contributionUnit',
    'contributionEndYear',
    'orionManagementStyleId',
  ]);

  const { mutateAsync } = useSaveDeferredCompensationMutation({ clientId });

  const baseAssetBucketOptions = [
    ...options.AvailableAssetBuckets.map(({ Title }, index) => ({
      label: Title,
      value: index,
      rightLabel:
        index === options.DeferredCompensationOptions.DefaultAssetBucketIndex
          ? 'Default'
          : undefined,
    })),
    EXCLUDE_OPT,
  ];

  const onSubmit = handleSubmit(async values => {
    const body: SaveDeferredCompensationDTO = {
      ClientId: clientId,
      Id: values.id,
      Name: values.name,
      Value: values.value ?? 0,
      Owner: values.earner,
      YearlyContribution: values.yearlyContribution,
      ContributionUnit: values.contributionUnit,
      ContributionStartAge: values.contributionStartAge,
      ContributionEndYear: values.contributionEndYear,
      DistributionStartAge: values.distributionStartAge,
      DistributionPeriod: values.distributionPeriod,
      AccumulationGrowthRate: values.accumulationGrowthRate,
      DistributionGrowthRate: values.distributionGrowthRate,
      CustomBucketIndex:
        values.customBucketIndex === EXCLUDE_OPT.value ? null : values.customBucketIndex,
      IsExcludedFromAssetBucketing: values.customBucketIndex === EXCLUDE_OPT.value,
      IsLiquid: values.isLiquid,
      IncludeInGrossEstate: values.includeInGrossEstate,
      OrionManagementStyleId: values.orionManagementStyleId,
      OwnerModel: values.ownerModel
        .filter(b => b.Name && b.Percentage)
        .map(owner => ({
          ...owner,
          Id: owner.Id ?? null,
          Name: owner.Name ?? '',
          OwnerType: owner.OwnerType ?? 0,
          Percentage: Number((parseFloat(owner.Percentage ?? '0') / 100).toFixed(4)),
        })),
    };

    try {
      await mutateAsync(body);

      toast.success(asset?.Id ? EDIT_ASSET_SUCCESS_MESSAGE : ADD_ASSET_SUCCESS_MESSAGE);

      onSuccess();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  });

  const isEarnerPrincipal =
    ownerModel[0].Name === options.DeferredCompensationOptions.earner.People[0].Name;

  const minDistributionStartAge = contributionEndYear
    ? contributionEndYear - new Date().getFullYear()
    : null;

  const principalEarnerAge = options.DeferredCompensationOptions.principalAge;
  const spouseEarnerAge = options.DeferredCompensationOptions.spouseAge;

  const earnerRetirementAge = isEarnerPrincipal
    ? options.DeferredCompensationOptions.principalRetirementAge
    : options.DeferredCompensationOptions.spouseRetirementAge;

  const earnerLifeExpectancy = isEarnerPrincipal
    ? options.DeferredCompensationOptions.principalLifeExpectancy
    : options.DeferredCompensationOptions.spouseLifeExpectancy;

  const contributionStartAgeOptions = isEarnerPrincipal
    ? options.DeferredCompensationOptions.contributionStartAgePrincipal
    : options.DeferredCompensationOptions.contributionStartAgeSpouse;

  const distributionStartAgeOptions = (isEarnerPrincipal
    ? options.DeferredCompensationOptions.distributionStartAgePrincipal
    : options.DeferredCompensationOptions.distributionStartAgeSpouse
  ).filter((option, index) => {
    if (!option.value || !minDistributionStartAge) {
      return true;
    }

    return index > minDistributionStartAge;
  });

  const getStartAgeYearLabel = (option: OptionType<number | null>) => {
    const AgeNumber = Number(option.value);

    let rightLabel: string | undefined;

    if (option.value && Number.isInteger(AgeNumber)) {
      if (isEarnerPrincipal && principalYob) {
        rightLabel = `Year ${AgeNumber + principalYob}`;
      } else if (spouseYob) {
        rightLabel = `Year ${AgeNumber + spouseYob}`;
      }
    }

    return rightLabel;
  };

  const contributionStartAgeOptionsWithRightLabel = contributionStartAgeOptions.map(option => ({
    ...option,
    rightLabel: getStartAgeYearLabel(option),
  }));

  const distributionStartAgeOptionsWithRightLabel = distributionStartAgeOptions.map(option => ({
    ...option,
    rightLabel: getStartAgeYearLabel(option),
  }));

  const contributionEndYearOptions = options.DeferredCompensationOptions.contributionEndYear.map(
    option => {
      const yearNumber = Number(option.value);

      let rightLabel: string | undefined;

      if (option.value && Number.isInteger(yearNumber)) {
        if (isEarnerPrincipal && principalYob) {
          rightLabel = `Age ${yearNumber - principalYob}`;
        } else if (spouseYob) {
          rightLabel = `Age ${yearNumber - spouseYob}`;
        }
      }

      return {
        ...option,
        rightLabel,
      };
    },
  );

  const initialPrincipalDistributionStartAgeValue =
    options.DeferredCompensationOptions.principalRetirementAge + 1;
  const initialSpouseDistributionStartAgeValue =
    options.DeferredCompensationOptions.spouseRetirementAge + 1;

  // calculate possible distribution periods based on the distribution start age
  const distributionPeriodOptions: OptionType<number>[] = range(
    1,
    1 + earnerLifeExpectancy - (distributionStartAge ?? earnerRetirementAge),
  ).map(i => ({
    label: i.toString(),
    value: i,
  }));

  const {
    AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    ManagementStyles,
    DisplayAssetBucketing: displayAssetBucketing,
  } = options;

  const managementStylesOptions = ManagementStyles.map(option => ({
    label: option.name,
    value: option.id,
    defaultBucketIndex: option.defaultBucket,
  }));

  const assetBucketOptions = getAssetBucketOptions({
    assetBucketOptions: baseAssetBucketOptions,
    assetBucketingByManagementStyle,
    managementStyleSelection,
    managementStylesOptions,
  });

  const onManagementSelect = baseOnManagementSelect({
    options: managementStylesOptions,
    setValue: (value: number) => setValue('customBucketIndex', value),
  });

  return {
    data: {
      isSubmitting,
      control,
      errors,
      principalEarnerAge,
      spouseEarnerAge,
      contributionUnit,
      defaultContributionUnitOption,
      contributionStartAgeOptions: contributionStartAgeOptionsWithRightLabel,
      distributionStartAgeOptions: distributionStartAgeOptionsWithRightLabel,
      contributionEndYearOptions,
      distributionPeriodOptions,
      assetBucketOptions,
      initialPrincipalDistributionStartAgeValue,
      initialSpouseDistributionStartAgeValue,
      isNewDeferredCompensation: !asset?.Id,
      managementStylesOptions,
      onManagementSelect,
      assetBucketingByManagementStyle,
      watchedManagementStyleSelection: managementStyleSelection,
      allowAssetBucketingCustomization: options.AllowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: { onSubmit, register, setValue },
  };
};
